import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { withAffiliate, visibleBuyLinks } from '../lib/retailers';
import { getCurrentUser } from '../lib/auth';
import { autoShelfBook } from './useAutoShelves';

// ─────────────────────────────────────────────────────────────────────────────
// useBooks — fetch books for matchmaker with tags and match calculation
// ─────────────────────────────────────────────────────────────────────────────
export function useBooks({ limit = 20, userPreferences = [] } = {}) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      
      const user = await getCurrentUser();
      
      let excludedBookIds = [];
      if (user) {
        const { data: libraryBooks } = await supabase
          .from('user_library')
          .select('book_id')
          .eq('user_id', user.id);
        
        // user_book_actions table may not exist yet — skip safely
        excludedBookIds = [
          ...(libraryBooks?.map(b => b.book_id) || []),
        ];
        excludedBookIds = [...new Set(excludedBookIds)];
      }
      
      let query = supabase
        .from('books')
        .select(`
          id,
          title,
          author_name,
          cover_image_url,
          blurb,
          avg_rating,
          book_tags (
            tropes (canonical_name, category)
          )
        `)
        .eq('status', 'live')
        .order('created_at', { ascending: false });
      
      if (excludedBookIds.length > 0) {
        query = query.not('id', 'in', `(${excludedBookIds.join(',')})`);
      }
      
      query = query.limit(limit * 3);
      
      const { data: booksData, error: booksError } = await query;

      if (booksError) {
        setError(booksError.message);
        setLoading(false);
        return;
      }

      const booksWithMatch = booksData.map(book => {
        const bookTropes = book.book_tags
          ?.map(bt => bt.tropes?.canonical_name)
          .filter(Boolean) || [];
        
        const overlap = userPreferences.filter(pref => 
          bookTropes.some(trope => trope.toLowerCase().includes(pref.toLowerCase()))
        ).length;
        
        const matchPercent = userPreferences.length > 0 
          ? Math.round((overlap / userPreferences.length) * 100)
          : 0;

        return {
          id: book.id,
          title: book.title,
          author: book.author_name,
          coverUrl: book.cover_image_url,
          blurb: book.blurb,
          avgRating: book.avg_rating,
          tropes: bookTropes,
          matchPercent,
        };
      });

      let finalBooks;
      
      if (userPreferences.length > 0) {
        const matchedBooks = booksWithMatch.filter(book => book.matchPercent > 0);
        
        if (matchedBooks.length >= limit) {
          matchedBooks.sort((a, b) => b.matchPercent - a.matchPercent);
          finalBooks = matchedBooks.slice(0, limit);
        } else {
          const unmatchedBooks = booksWithMatch
            .filter(book => book.matchPercent === 0)
            .slice(0, limit - matchedBooks.length);
          
          matchedBooks.sort((a, b) => b.matchPercent - a.matchPercent);
          finalBooks = [...matchedBooks, ...unmatchedBooks];
        }
      } else {
        finalBooks = booksWithMatch.slice(0, limit);
      }

      setBooks(finalBooks);
      setLoading(false);
    }
    
    fetch();
   
  }, [limit, userPreferences.join(',')]);

  return { books, loading, error };
}

// ─────────────────────────────────────────────────────────────────────────────
// useBookSearch — search books by title, author, or tags
// ─────────────────────────────────────────────────────────────────────────────
export function useBookSearch() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = async (query, includeTags = [], excludeTags = []) => {
    if (!query.trim() && includeTags.length === 0) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: searchError } = await supabase
        .rpc('search_books', { 
          search_query: query.trim(),
          limit_count: 20,
          include_tags: includeTags,
          exclude_tags: excludeTags,
        });

      if (searchError) throw searchError;

      setResults(formatSearchRows(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * The same search, told which section each tag came from.
   *
   * search() above sends one flat include array, and search_books passes a
   * book that carries ANY tag in it. That is right for the tag cloud, where
   * every pill is one undifferentiated "show me this", and wrong for the
   * filter sheet, where picking a genre AND a trope has to mean both.
   *
   * `facets` is passed straight through to search_books_faceted, whose
   * parameter names it already uses, so there is nothing to keep in step here.
   * The section-by-section rule lives in matching_book_ids
   * (20260905070000_faceted_book_filters.sql) and in nothing else.
   *
   * This is NOT a second search path: the two RPCs read the same tables with
   * the same status rule, and the faceted one is a superset — passing only
   * any_tags reproduces search_books exactly.
   */
  const searchFaceted = async (facets, { limit = 20 } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: searchError } = await supabase
        .rpc('search_books_faceted', { ...facets, limit_count: limit });

      if (searchError) throw searchError;
      setResults(formatSearchRows(data));
    } catch (err) {
      setError(err.message);
      // An empty shelf and a failed search look identical to whoever is
      // reading it, so the results are cleared and `error` is what the caller
      // has to check before saying "no books found".
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return { results, loading, error, search, searchFaceted };
}

// Both search paths return the same columns, so they format the same way.
// Deduplicated by book_id in case the SQL returns a book twice.
function formatSearchRows(data) {
  const seen = new Set();
  return (data || [])
    .filter(book => {
      if (seen.has(book.book_id)) return false;
      seen.add(book.book_id);
      return true;
    })
    .map(book => ({
      id: book.book_id,
      title: book.title,
      author: book.author_name,
      coverUrl: book.cover_image_url,
      description: book.blurb, // search_books()/search_books_faceted() RPCs still return `blurb` (DB column untouched)
      avgRating: book.avg_rating,
      tropes: book.matched_tags || [],
      matchPercent: 94,
    }));
}

// ─────────────────────────────────────────────────────────────────────────────
// useLibrary — NO CACHING, just fresh data every time
// ─────────────────────────────────────────────────────────────────────────────
export function useLibrary() {
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let channel;
    let userId = null;

    async function fetchLibrary() {
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      userId = user.id;

      const { data, error: fetchError } = await supabase
        .from('user_library')
        .select(`
          id, 
          shelf, 
          rating, 
          date_added, 
          date_read, 
          data_source,
          book_id,
          csv_book_id,
          books (
            id, 
            title, 
            author_name, 
            avg_rating, 
            cover_image_url, 
            page_count,
            genres,
            created_at,
            book_tags (
              tropes (canonical_name)
            )
          ),
          csv_book_imports (
            id,
            title,
            author_name,
            cover_image_url,
            page_count,
            avg_rating,
            created_at,
            genres,
            trope_ids
          )
        `)
        .eq('user_id', user.id)
        .order('date_added', { ascending: false });

      if (fetchError) {
        setError(fetchError.message);
      } else {
        setLibrary(data || []);
      }
      setLoading(false);
    }

    async function setup() {
      setLoading(true);
      await fetchLibrary();

      // ── Subscribe AFTER initial fetch so we have userId ──────────────
      if (userId) {
        channel = supabase
          .channel(`library-${userId}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'user_library',
            filter: `user_id=eq.${userId}`,
          }, (payload) => {
            console.log('🔴 Real-time event received:', payload);
            fetchLibrary();
          })
          .subscribe((status) => {
            console.log('📡 Subscription status:', status);
          });
      }
    }

    setup();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  // ── Add platform book to shelf ────────────────────────────────────────────
  // Called from MatchMeCard (TBR/Love swipe) and anywhere else
  // After adding, auto-sorts the book into genre/trope smart shelves
  const addToShelf = async (bookId, shelf = 'tbr') => {
    const user = await getCurrentUser();
    if (!user) return { error: 'Not logged in' };

    const { data, error } = await supabase
      .from('user_library')
      .upsert({ 
        user_id: user.id, 
        book_id: bookId,
        csv_book_id: null,
        shelf 
      })
      .select(`
        id, 
        shelf, 
        rating, 
        date_added, 
        date_read,
        book_id,
        csv_book_id,
        books (
          id, 
          title, 
          author_name, 
          avg_rating, 
          cover_image_url, 
          page_count,
          genres,
          created_at,
          book_tags (
            tropes (canonical_name)
          )
        ),
        csv_book_imports (
          id,
          title,
          author_name,
          cover_image_url,
          page_count,
          avg_rating,
          created_at,
          genres,
          trope_ids
        )
      `)
      .single();

    if (!error && data) {
      setLibrary(prev => {
        // Replace existing entry if already in library, otherwise add
        const exists = prev.find(item => item.id === data.id);
        if (exists) return prev.map(item => item.id === data.id ? data : item);
        return [...prev, data];
      });

      // ── Auto-shelf: fetch tropes directly from book_tags then shelve ───────
      // genres column is empty on platform books for now — use tropes only
      // Do a fresh query instead of relying on nested upsert response
      if (data.book_id) {
        supabase
          .from('book_tags')
          .select('tropes (canonical_name)')
          .eq('book_id', data.book_id)
          .eq('tag_type', 'trope')
          .then(({ data: tagData }) => {
            const tropes = (tagData || [])
              .map(bt => bt.tropes?.canonical_name)
              .filter(Boolean);
            console.log('🏷️ Tropes found for auto-shelf:', tropes);
            if (tropes.length > 0) {
              autoShelfBook(data.id, { genres: [], tropes }, user.id)
                .catch(err => console.warn('Auto-shelf error:', err.message));
            } else {
              console.log('ℹ️ No tropes tagged for book_id:', data.book_id, '— no auto-shelf created');
            }
          })
          .catch(err => console.error('book_tags fetch for auto-shelf failed:', err.message));
      }

      console.log('✅ Book added to library:', data.books?.title || data.id);
    } else {
      console.error('❌ Error adding book:', error);
    }
    
    return { error };
  };

  // ── Remove from shelf (handles both platform and CSV books) ───────────────
  const removeFromShelf = async (bookId) => {
    const user = await getCurrentUser();
    if (!user) return;

    const entry = library.find(item => 
      item.books?.id === bookId || item.csv_book_imports?.id === bookId
    );

    if (!entry) return;

    await supabase
      .from('user_library')
      .delete()
      .eq('user_id', user.id)
      .eq('id', entry.id);

    setLibrary(prev => prev.filter(b => b.id !== entry.id));
  };

  // ── Check if book is in library ───────────────────────────────────────────
  const isInLibrary = (bookId) => {
    return library.some(item => 
      item.books?.id === bookId || item.csv_book_imports?.id === bookId
    );
  };

  // ── Helper to get normalised book data (works for both platform + CSV) ────
  const getBookData = (item) => {
    const bookData = item.books || item.csv_book_imports;
    return {
      id: bookData?.id,
      title: bookData?.title,
      author: bookData?.author_name,
      cover: bookData?.cover_image_url,
      pageCount: bookData?.page_count,
      avgRating: bookData?.avg_rating,
      createdAt: bookData?.created_at,
      genres: bookData?.genres || [],
      source: item.book_id ? 'platform' : 'csv',
      tropes: item.books?.book_tags?.map(tag => tag.tropes?.canonical_name).filter(Boolean) || [],
      tropeIds: item.csv_book_imports?.trope_ids || []
    };
  };

  return { 
    library, 
    loading, 
    error, 
    addToShelf, 
    removeFromShelf, 
    isInLibrary,
    getBookData
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// useCustomShelves — user's custom shelves
// ─────────────────────────────────────────────────────────────────────────────
export function useCustomShelves() {
  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      const { data } = await supabase
        .from('custom_shelves')
        .select('id, name, emoji, color')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      setShelves(data || []);
      setLoading(false);
    }
    fetch();
  }, []);

  const createShelf = async (name, emoji = '📚', color = '#d97706') => {
    const user = await getCurrentUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('custom_shelves')
      .insert({ user_id: user.id, name, emoji, color })
      .select()
      .single();

    if (!error) setShelves(prev => [...prev, data]);
    return { data, error };
  };

  return { shelves, loading, createShelf };
}

// ─────────────────────────────────────────────────────────────────────────────
// useUserBookTags — NO CACHING
// ─────────────────────────────────────────────────────────────────────────────
export function useUserBookTags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState([]);

  useEffect(() => {
    async function fetch() {
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      const { data } = await supabase
        .from('user_book_tags')
        .select('book_id, tag')
        .eq('user_id', user.id);

      const tagsData = data || [];
      const allTagsData = [...new Set(tagsData.map(t => t.tag))];

      setTags(tagsData);
      setAllTags(allTagsData);
      setLoading(false);
    }
    fetch();
  }, []);

  const addTag = async (bookId, tag) => {
    const user = await getCurrentUser();
    if (!user) return;
    
    await supabase.from('user_book_tags').upsert({ user_id: user.id, book_id: bookId, tag });
    setTags(prev => [...prev, { book_id: bookId, tag }]);
  };

  const removeTag = async (bookId, tag) => {
    const user = await getCurrentUser();
    if (!user) return;
    
    await supabase.from('user_book_tags').delete()
      .eq('user_id', user.id).eq('book_id', bookId).eq('tag', tag);
    setTags(prev => prev.filter(t => !(t.book_id === bookId && t.tag === tag)));
  };

  const booksByTag = (tag) => tags.filter(t => t.tag === tag).map(t => t.book_id);

  return { tags, allTags, loading, addTag, removeTag, booksByTag };
}

// ─────────────────────────────────────────────────────────────────────────────
// useReadingChallenge — NO CACHING
// ─────────────────────────────────────────────────────────────────────────────
export function useReadingChallenge() {
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      const year = new Date().getFullYear();

      const { data: challengeData } = await supabase
        .from('reading_challenges')
        .select('goal')
        .eq('user_id', user.id)
        .eq('year', year)
        .single();

      if (challengeData) {
        const { count } = await supabase
          .from('user_library')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('shelf', 'read')
          .gte('date_read', `${year}-01-01`);

        setChallenge({
          year,
          goal: challengeData.goal,
          completed: count || 0,
        });
      } else {
        setChallenge(null);
      }
      
      setLoading(false);
    }
    fetch();
  }, []);

  return { challenge, loading };
}

// ─────────────────────────────────────────────────────────────────────────────
// getPurchaseLinks — fetch purchase links for a book with affiliate codes
// ─────────────────────────────────────────────────────────────────────────────
export async function getPurchaseLinks(bookId) {
  // Buy links live in two places and nothing keeps them in step.
  //
  //   purchase_links (table)  — where the original bulk import landed, and the
  //                             only source this function used to read.
  //   books.purchase_links    — a jsonb column, and where the author app writes
  //     (jsonb column)          every link an author types in (see reanlo-author
  //                             src/hooks/useBooks.js).
  //
  // Reading only the table meant every link an author has entered since the
  // import was invisible to readers. Confirmed against production: books with
  // links in the column had zero rows in the table, going back months.
  //
  // Both are read and merged here. That is deliberately not a data migration —
  // consolidating onto one store touches the author app and needs a backfill,
  // and readers should not wait for that to see links that already exist.
  const [tableRes, bookRes] = await Promise.all([
    // Ordered by id so the copy kept when an address is saved twice in the table is the
    // same one the admin count keeps (buy_link_tally in the ticket 48 migration orders by id).
    supabase.from('purchase_links').select('retailer, url').eq('book_id', bookId).order('id'),
    supabase.from('books').select('purchase_links').eq('id', bookId).maybeSingle(),
  ]);

  const fromTable = tableRes.error ? [] : (tableRes.data || []);
  const fromBook  = bookRes.error || !Array.isArray(bookRes.data?.purchase_links)
    ? []
    : bookRes.data.purchase_links;

  // Same book can carry the same URL in both stores, so it is offered once, and
  // anything that is not a link to this actual book is dropped before it can reach
  // a Buy button. Both happen in visibleBuyLinks (src/lib/retailers.js) so the admin
  // count of books with nowhere to buy follows the same rule. Filtering here rather
  // than at each render site means every caller gets the same guarantee and none of
  // them needs a fallback.
  return visibleBuyLinks(fromTable, fromBook)
    .map(link => ({ retailer: link.retailer, url: withAffiliate(link.url) }));
}